import { ChevronRightIcon, MinusIcon, PlusIcon } from "@heroicons/react/solid";
import Collapse from "@material-ui/core/Collapse";
import clsx from "clsx";
import firebase from "firebase/app";
import { useRouter } from "next/router";
import { FC, useMemo } from "react";

import { AnalyticsEventEnum } from "@/consts/AnalyticsEventEnum";

type SettingMenuItemClassProps = {
  containerClassName?: string;
  labelClassName?: string;
};

export type SettingMenuItemProps = {
  label: string;
  route?: string;
  subPaths?: { label: string; path: string; onClick?: () => void }[];
  isOpen?: boolean;
  setOpenRoute?: (route: string) => void;
  classes?: SettingMenuItemClassProps;
  handleDrawerClose?: () => void;
};

export const SettingMenuItem: FC<SettingMenuItemProps> = (props) => {
  const router = useRouter();

  const {
    route,
    label,
    subPaths,
    isOpen,
    setOpenRoute = () => {},
    handleDrawerClose = () => {},
  } = props;
  const { containerClassName, labelClassName } = props.classes ?? {};

  const onClick = !route
    ? () => {}
    : !!subPaths
    ? () => {
        firebase
          .analytics()
          .logEvent(AnalyticsEventEnum.SubmenuToggleItem, { label, route });
        handleDrawerClose();
        setOpenRoute(route);
      }
    : () => {
        firebase
          .analytics()
          .logEvent(AnalyticsEventEnum.SubmenuNavigateToRoute, { route });
        handleDrawerClose();
        router.push(route);
      };

  const activeStyle = useMemo(() => {
    if ((!isOpen || !subPaths) && route === router.asPath)
      return {
        backgroundColor: "bg-primary-700",
        span: "text-white font-medium",
        icon: "text-white",
      };

    return {
      backgroundColor: "",
      span: "text-gray-900",
      icon: "text-gray-300",
    };
  }, [isOpen, subPaths, route, router.asPath]);

  return (
    <div className={clsx("select-none")}>
      <div
        onClick={onClick}
        className={clsx(
          "flex justify-between items-center p-2 px-4",
          !!route && "touchable",
          activeStyle.backgroundColor,
          containerClassName,
        )}
      >
        <span className={clsx("text-base", activeStyle.span, labelClassName)}>
          {label}
        </span>

        {!subPaths ? null : isOpen ? (
          <MinusIcon className={clsx("w-[18px]", activeStyle.icon)} />
        ) : (
          <PlusIcon className={clsx("w-4", activeStyle.icon)} />
        )}
      </div>

      {!!subPaths && (
        <Collapse in={isOpen}>
          {subPaths.map((item, idx) => {
            const isActivePath = router.asPath === item.path;
            const onClickPath = item.onClick ?? (() => router.push(item.path));

            return (
              <div
                key={idx}
                onClick={onClickPath}
                className={clsx(
                  "flex items-center p-2 px-4 touchable",
                  isActivePath && "bg-primary",
                )}
              >
                <span>
                  <ChevronRightIcon
                    className={clsx(
                      "w-4 h-4 text-gray-500",
                      isActivePath ? "text-white" : "text-gray-500",
                    )}
                  />
                </span>
                <span
                  className={clsx(
                    "ml-2 text-base ",
                    isActivePath ? "text-white" : "text-gray-500",
                  )}
                >
                  {item.label}
                </span>
              </div>
            );
          })}
        </Collapse>
      )}
    </div>
  );
};
